// 'use client' is load-bearing (#118 slice 8): Shop is hosted directly by the
// SERVER component app/[[...slug]]/RenderSectionsServer.jsx (ISLAND_SECTIONS), so
// it must declare its own client boundary. On the client RenderSections path it
// already runs inside the RegionPageClient island, where this directive is a
// no-op — same as FaqPage/ContactForm/SecondaryNavbar and the other hosted islands.
'use client'

import React, { useEffect, useState } from 'react'
import Text from '../../Text'
import ShopItem from '../../ShopItem'
import Spacer from '../../Spacer'
import ShippingInfo from './ShippingInfoSection'
import { useSessionStore } from '../../../store/sessionStore'
import { useRegionStore } from '../../../store/regionStore'
import { useRegionShippingInfo, useRegionCountryCode } from '../../RegionGeo'
import { useRegionOffers } from '../../RegionOffers'
import { useUrlRegion } from '../../../utils/useCurrentRegion'
import styles from './styles.module.css'
import { buildAddToCartEvent } from '../../../utils/analytics'
import {
  formatShopItemDiscountPrice,
  formatShopItemOriginalPrice, getShopItemOriginalPrice,
  getShopItemPrice,
} from '../../../utils/shopItemsAndCart'
import { isComingSoon, isOutOfStock, isPreOrder } from '../../../utils/shopItems'
import { buildShopItemListJsonLd } from '../../../utils/productJsonLd'
import ErrorBoundary from '../../ErrorBoundary'
import CantShipNotice from '../../CantShipNotice'
import Perks from './Perks'
import DiscountInfo from './DiscountInfo'
import { $textPrimary } from '../../../styles/colors/semantics'
import BF2025 from './BF2025'
import ShopItemBF2025 from '../../ShopItem/ShopItemBF2025'
import BF2024Hero from '../BF2024Hero' // direct import, not the sections barrel (#130 de-barrel)
import ShopItemBF2025Gift from '../../ShopItem/ShopItemBF2025Gift'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../utils/dataLayer'
import SaleShopBanner from '../Sale/SaleShopBanner'
import useSaleClock from '../../SaleCampaign/useSaleClock'
import { getSaleCardTag, getSaleBonus, offerItemType } from '../../../lib/saleCampaign'

// Reproduce the former styled `ShopItems` `numItems` switch (min-768px child width)
// as a CSS custom property. Returns undefined for the fall-through (no width set),
// matching the old styled function returning undefined.
const shopItemWidthFor = (numItems) => {
  if (numItems === 2) return 'calc(40% - 16px)'
  if (numItems === 3) return 'calc(33.33% - 16px)'
  if (numItems === 4 || numItems === 1 || numItems > 4) return 'calc(25% - 16px)'
  return undefined
}

// `shopItems` (Overmind→server-props migration, slice 1): the grid-projected
// offers resolved server-side (lib/regionPageData) and threaded as a PROP by the
// RSC dispatch (app/[[...slug]]/RenderSectionsServer.jsx). The GRID/categories/
// JSON-LD render from this prop — NOT a render-time `state.shopItems` Overmind
// read — so the catalog paints in the SSR HTML without depending on Overmind
// hydration timing. It is byte-identical to state.shopItems (same projection), so
// the island's server render === its client first render. `region` (the URL-
// derived page region, also threaded by the dispatch) precomputes each card's
// checkout href off the render path. On the CLIENT path (RenderSections.js, which
// passes neither prop) both fall back to Overmind/URL exactly as before — no
// regression. Genuinely-interactive/per-user bits (shipping notices, cart,
// countries, discount, category filter) keep reading Overmind below.
function Shop({ blackFridaySubtitleHl, blackFridayHeaderImageCs, shopItems: shopItemsProp = null, region = null,
                saleCampaign = null, saleNowMs = null, saleClockOffsetMs = 0 }) {
  // discountCode moved off Overmind to the Zustand session store (Phase C2 part 1).
  const discountCode = useSessionStore((s) => s.discountCode)
  // Region geo migrated off Overmind (Phase C2 part 2): countries/shippingCountry
  // from the Zustand region store; shippingInfo/newRegion from the RegionGeo seam.
  // Offers migrated off Overmind (Phase D1): the client-path fallback offers +
  // the loading flag now come from the RegionOffers seam / offers store (the
  // server-prop `shopItemsProp` is still the SSR/RSC grid source and wins).
  const countries = useRegionStore((s) => s.countries)
  const shippingCountry = useRegionStore((s) => s.shippingCountry)
  const shippingInfo = useRegionShippingInfo()
  const newRegion = useRegionCountryCode()
  const { offers, offersLoading, offersFull } = useRegionOffers()
  // URL-derived fallback so SSR === first client render when no region prop is threaded.
  const urlRegion = useUrlRegion()
  const pageRegion = region || urlRegion
  const [selectedCategory, setSelectedCategory] = useState(null)

  // MOUNTED GATE (mirrors SpecsPage): guaranteed false on the SSR pass AND the
  // client's first (hydration) render, so both take the server-prop branch below
  // no matter how fast the post-mount offers fetch resolves — SSR HTML === first
  // client render (no #418/#422 mismatch).
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  // `shopItemsProp` is the SSR/RSC grid priced for the REGION REPRESENTATIVE (e.g.
  // DE on /eu), which folds every EU-region country — incl. the UK — onto one
  // catalog. That is correct for SSR/first paint (hydration-safe, cacheable), but
  // it means a UK visitor's grid would show DE availability: items NOT sold to the
  // UK (sim rigs — price:null / availabilityStatus UNAVAILABLE) wrongly appear,
  // and UK-only stock is hidden. Once the offers store holds the FULL offers for
  // the visitor's PRECISE shipping country (loaded post-mount by
  // refineForShippingCountry), prefer them so the grid's existing
  // `!!price` / `availabilityStatus !== 'UNAVAILABLE'` filters hide the
  // region-unavailable items. Gated on `mounted` so it only ever swaps AFTER
  // hydration.
  const refinedOffers = mounted && offersFull && Array.isArray(offers) && offers.length ? offers : null
  const baseShopItems = refinedOffers || shopItemsProp || offers || []

  // Seasonal sale (lib/saleCampaign): the campaign doc is injected server-side;
  // the clock starts at the SERVER render time (saleNowMs) so SSR === first
  // client render, then ticks once a minute (phase / deal state only; the
  // countdown digits tick on their own, see SaleCountdown `live`, so the whole
  // product grid does not re-render every second). A sale item the backend prices below regular gets
  // the sale pill through the existing catalog cardTag slot; a running limited
  // deal also gets `saleDeal` (second pill line + countdown to the deal end).
  // No campaign => shopItems is the same array as before.
  const saleClock = useSaleClock(saleNowMs, saleClockOffsetMs, 60000)
  const shopItems = saleCampaign
    ? baseShopItems.map((i) => {
      const tag = getSaleCardTag(saleCampaign, saleClock.nowMs, pageRegion, i)
      const saleBonus = getSaleBonus(saleCampaign, saleClock.nowMs, pageRegion, offerItemType(i))
      if (!tag && !saleBonus) return i
      const saleDeal = tag && tag.endsAtMs
        ? { sub: tag.sub, endsAtMs: tag.endsAtMs, nowMs: saleClock.nowMs, mounted: saleClock.mounted }
        : null
      return { ...i, ...(tag ? { cardTag: tag.label, cardTagVariant: tag.variant } : null), saleBonus, saleDeal }
    })
    : baseShopItems

  useEffect(() => {
    if (!shopItems || !shopItems.length) return
    const listItems = selectedCategory
      ? shopItems.filter(i => i.category && i.category.type === selectedCategory.type)
      : shopItems
    pushEcommerceEvent('view_item_list', {
      ...buildEcommercePayload(listItems),
      item_list_id: selectedCategory ? selectedCategory.type : 'shop',
      item_list_name: selectedCategory ? selectedCategory.displayName : 'Shop',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopItems && shopItems.length, selectedCategory && selectedCategory.type])

  // #418: gate the loader on THIS section's OWN data (shopItems), NOT the global
  // state.isLoading flag. shopItems is SSR-hydrated for /shop (regionMutations),
  // so it is PRESENT on the client's first render — the section renders content
  // on SSR and on the client's first render identically (no hydration mismatch).
  // The loader now only appears when shopItems is genuinely absent, which is the
  // SAME on SSR and client-first render (shopItems is not conjured client-side
  // before its own fetch resolves). The old `if (state.isLoading)` emitted a
  // spinner into the SSR HTML whenever ANY section's fetch had flipped the global
  // flag — even though this section's data was already hydrated — which is the
  // latency-only #418 cascade.
  if (!shopItems || !shopItems.length) {
    return (
      <div className={styles.wrapper}>
        {
          (() => {
            if (countries && countries.length && newRegion) {
              return (
                <div className={styles.shippingInfoWrapper}>
                  <ShippingInfo
                    countries={countries}
                    selectedCountryCode={shippingCountry}
                    shippingInfo={shippingInfo}
                    isLoading={offersLoading}
                  />
                </div>
              )
            }
          }) ()
        }
        <div className={styles.contentWrapper}>
          <Spacer size={80} />
          <div className={styles.shopItems}>
            {
              Array(15)
                .fill()
                .map((e, i) => <ShopItem key={'loader-' + i} isLoading={true}/>)
            }
          </div>
        </div>
      </div>
    )
  }

  // `sectionKey` is the React list key for this section — renderShopSection is
  // called inside `[...allCategories].map(...)` below, so each returned element
  // MUST carry a stable key or React logs "Each child in a list should have a
  // unique key" (a first-party console.error the #114 gate reds; #127). The bare
  // `<>` fragment cannot take a key, so the multi-element branch returns a keyed
  // <React.Fragment>. Key is the category type/title (stable + unique per shop).
  const renderShopSection = (title, items, highlightItems, isFirstSection = false, sectionKey = null) => {
    if (title === 'Holiday Sale') {
      return <div className="mt-12 lg:mt-24" key={sectionKey || undefined}>
      <BF2024Hero imageCs={blackFridayHeaderImageCs} isCompact={true} subtitleHl={blackFridaySubtitleHl}/>
      <BF2025 title={title} items={items} isLoading={offersLoading}/>
      </div>
    }

    const shownItems = items
      .filter(i => i.displayOnShop)
      .filter(i => !!i.price)
      // catalog `hiddenInRegions` (e.g. ['eu']): keep a card off one region's grid
      // while it stays listed everywhere else.
      .filter(i => !(pageRegion && Array.isArray(i.hiddenInRegions) && i.hiddenInRegions.includes(pageRegion)))

    if (shownItems && shownItems.length) {
      let itemsPerRow = shownItems.length
      if (shownItems.length >= 4) {
        itemsPerRow = 4
      }

      return (
        <React.Fragment key={sectionKey || undefined}>
          <div className={styles.shopItemsTitle}>
            <Text type="heading-2">{title}</Text>
          </div>
          <div className={styles.shopItems} style={{ '--shop-item-width': shopItemWidthFor(shownItems.length) }}>
            {shownItems
              .filter(i => i.displayOnShop)
              .filter(i => !!i.price)
              .map((shopItem, index) => (
                <ErrorBoundary key={shopItem.id || shopItem.productSetId || shopItem.itemType || index}>
                  {renderShopItem(shopItem, offersLoading, false, highlightItems, isFirstSection ? index : -1, shopItems, region)}
                </ErrorBoundary>
              ))}
          </div>
        </React.Fragment>
      )
    }
  }

  const highlightedItems = shopItems.filter(i => i.isHighlighted)
  const nonHighlightedItems = shopItems.filter(i => !i.isHighlighted)
  const allCategories = [...new Map(nonHighlightedItems.filter(i => i.category && i.availabilityStatus !== 'UNAVAILABLE').map(i => [i.category.type, i.category])).values()]
  allCategories.sort((a, b) => a.order - b.order)

  // ItemList structured data for the whole catalog grid (audit C-H2 #2). Built
  // from the SSR-hydrated shopItems so it ships in the SSR HTML (raw <script>,
  // task #119 — next-seo crashes under App Router SSR). Currency is per-item, so
  // /us=USD, /eu=EUR, /au=AUD, /world=EUR.
  const itemListJsonLd = buildShopItemListJsonLd({
    items: shopItems,
    name: 'VRS Shop',
  })

  return (
    <div className={styles.wrapper}>
      {itemListJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd).replace(/</g, '\\u003c') }}
        />
      )}
      <div className={styles.shippingInfoWrapper}>
        <ShippingInfo
          countries={countries}
          selectedCountryCode={shippingCountry}
          shippingInfo={shippingInfo}
          isLoading={false}
        />
      </div>
      <div className={styles.categories}>
        { [...allCategories].map(c => {
          const isSelected = !!selectedCategory && selectedCategory.type === c.type
          const isDark = c.displayName === 'Black Friday Deals'
          const isSpecial = c.displayName === 'Holiday Sale'
          const chipClasses = [styles.categoryChip]
          if (isSelected) chipClasses.push(styles.categoryChipSelected)
          if (isDark) chipClasses.push(styles.categoryChipDark)
          return (
          <div className={chipClasses.join(' ')}
                        onClick={() => setSelectedCategory(selectedCategory && selectedCategory.type === c.type ? null : c)}
                        key={c.type}
          >
            <div className={`${styles.categoryImageWrapper}${isSpecial ? ' ' + styles.categoryImageWrapperSpecial : ''}`}>
              {/* category pictogram is normalized to a ResolvedImage descriptor
                  server-side (task #67); CategoryImage is a styled bg taking a URL. */}
              <img className={styles.categoryImage} style={{ '--category-image-size': isSpecial ? '32px' : '48px' }} width={isSpecial ? 32 : 48} height={isSpecial ? 32 : 48} src={(c.pictogram && c.pictogram.src) || c.pictogramUrl} alt={c.displayName}/>
            </div>
            <Text type="small">{c.displayName}</Text>
          </div>
          )
        })}
      </div>
      <div className={styles.contentWrapper}>
        {saleCampaign && (
          <SaleShopBanner campaign={saleCampaign} nowMs={saleClock.nowMs} mounted={saleClock.mounted} region={pageRegion}/>
        )}
        <CantShipNotice/>
        <Perks
          shippingInfo={shippingInfo}
          taxIncluded={shopItems && shopItems.length && shopItems[0].taxIncluded}
        />
        <DiscountInfo code={discountCode}/>
        {/*{ state.affiliateCode && state.affiliateDiscountPercentage > 0 && (*/}
        {/*  <AffiliateInfo>*/}
        {/*    <Text type="small">You&apos;re shopping with affiliate link &quot;{state.affiliateCode}&quot; and will receive a {state.affiliateDiscountPercentage}% discount at checkout</Text>*/}
        {/*  </AffiliateInfo>*/}
        {/*)}*/}
        {
          !!selectedCategory
            ? <> { renderShopSection(selectedCategory.displayName, shopItems.filter(i => i.category && i.category.type === selectedCategory.type), false, true) } </>
            : (
              <>
                {/*{ renderShopSection('Spotlight', highlightedItems, true) }*/}
                {/*{ [...allCategories].map(c => renderShopSection(c.displayName, nonHighlightedItems.filter(i => i.category.type === c.type)))}*/}
                { [...allCategories].map((c, categoryIndex) => renderShopSection(c.displayName, shopItems.filter(i => i.category && i.category.type === c.type), false, categoryIndex === 0, c.type))}
              </>
            )
        }
        {/*<CartModifiedAlert />*/}
      </div>
    </div>
  )
}

// `items` (Overmind→server-props migration): the resolved shopItems array the
// sameSet / color-variant lookups filter over — passed explicitly by both callers
// (the Shop grid + BF2025) so these derivations never read a render-time
// `state.shopItems` Overmind value. `isLoading` (Phase D1: the offers store's
// loading flag, formerly state.shopItemsLoading) drives the card skeleton. `region`
// (URL-derived, threaded from the RSC dispatch) precomputes the card's checkout
// href off the render path — passed straight to <ShopItem region={region}>.
export const renderShopItem = (shopItem, isLoading, dark, isHighlighted, index = 0, items = null, region = null) => {
  const shopItems = items || []
  // imageOverride / galleryImages[] / imageUrl are ResolvedImage descriptors
  // (normalized server-side, task #67). mainImage/hoverImage ARE descriptors now.
  const mainImage = shopItem.imageOverride
      ? shopItem.imageOverride
      : (shopItem.galleryImages && shopItem.galleryImages.length ? shopItem.galleryImages[0] : shopItem.imageUrl)
  const hoverImage = shopItem.galleryImages && shopItem.galleryImages.length ? shopItem.galleryImages[1] : null

  let outOfStock
  if (shopItem.outOfStockOnlyIfEveryOfferFromSetIsOutOfStock) {
    let sameSetItems = shopItems.filter(i => i.productSetId === shopItem.productSetId)
    outOfStock = sameSetItems.every(i => isOutOfStock(i))
  } else {
    outOfStock = isOutOfStock(shopItem)
  }

  // Calculate color variants for items with colorInfo
  let colorVariants = null
  let currentColorName = null
  if (shopItem.colorInfo && shopItem.colorInfo.colorName) {
    currentColorName = shopItem.colorInfo.colorName
    // Find all items with same productSetId that have colorInfo
    const sameProductItems = shopItems.filter(i =>
      i.productSetId === shopItem.productSetId &&
      i.colorInfo &&
      i.colorInfo.colorName
    )
    // Extract unique colors
    const colorMap = new Map()
    sameProductItems.forEach(item => {
      if (!colorMap.has(item.colorInfo.colorName)) {
        colorMap.set(item.colorInfo.colorName, {
          colorName: item.colorInfo.colorName,
          colorHex: item.colorInfo.colorHex
        })
      }
    })
    colorVariants = Array.from(colorMap.values())
  }

  let outOfStockMessage = shopItem.outOfStockMessage
  if (shopItem.availabilityStatus === 'UNAVAILABLE') {
    outOfStockMessage = 'Not available'
  } else if (shopItem.availabilityStatus === 'SOLD_OUT') {
    outOfStockMessage = 'Sold out'
  }
  // Retired line (catalog `endOfLife`): once it sells out it is gone for good,
  // so the grey pill says so instead of implying a restock.
  if (shopItem.endOfLife && outOfStock) {
    outOfStockMessage = 'Discontinued'
  }

  if (!!shopItem.externalLink) {
    return <ShopItemBF2025Gift
      isLoading={isLoading}
      key={shopItem.name}
      name={shopItem.namePublicShop || shopItem.name}
      freeBonuses={shopItem.freeBonuses}
      saleBonus={shopItem.saleBonus}
      typeNames={shopItem.typeNames}
      mainImage={mainImage}
      hoverImage={hoverImage}
      priceObj={getShopItemPrice(shopItem)}
      price={formatShopItemOriginalPrice(shopItem)}
      discountPriceObj={shopItem.discountPrice}
      regularPriceObj={getShopItemOriginalPrice(shopItem)}
      discountPrice={formatShopItemDiscountPrice(shopItem)}
      hasSpecialOffer={false && shopItem.hasSpecialOffer}
      taxIncluded={shopItem.taxIncluded}
      optionalFor={shopItem.optionalFor && shopItem.optionalFor.itemTypeName}
      outOfStock={outOfStock}
      outOfStockMessage={outOfStockMessage}
      isNew={isComingSoon(shopItem)}
      isPreOrder={isPreOrder(shopItem)}
      productSetId={shopItem.productSetId}
      event={() => buildAddToCartEvent(shopItem)}
      dark={true}
      isHighlighted={isHighlighted}
      pricePrefix={shopItem.pricePrefix}
      timeLimit={shopItem.timeLimit}
      stockLimit={shopItem.stockLimit}
      stockLimitLeft={shopItem.stockLimitLeft}
      externalLink={shopItem.externalLink}
    />
  } if (dark) {
    return <ShopItemBF2025
      isLoading={isLoading}
      key={shopItem.name}
      name={shopItem.namePublicShop || shopItem.name}
      freeBonuses={shopItem.freeBonuses}
      saleBonus={shopItem.saleBonus}
      typeNames={shopItem.typeNames}
      mainImage={mainImage}
      hoverImage={hoverImage}
      priceObj={getShopItemPrice(shopItem)}
      price={formatShopItemOriginalPrice(shopItem)}
      discountPriceObj={shopItem.discountPrice}
      regularPriceObj={getShopItemOriginalPrice(shopItem)}
      discountPrice={formatShopItemDiscountPrice(shopItem)}
      hasSpecialOffer={false && shopItem.hasSpecialOffer}
      taxIncluded={shopItem.taxIncluded}
      optionalFor={shopItem.optionalFor && shopItem.optionalFor.itemTypeName}
      outOfStock={outOfStock}
      outOfStockMessage={outOfStockMessage}
      isNew={isComingSoon(shopItem)}
      isPreOrder={isPreOrder(shopItem)}
      productSetId={shopItem.productSetId}
      event={() => buildAddToCartEvent(shopItem)}
      dark={false}
      isHighlighted={isHighlighted}
      pricePrefix={shopItem.pricePrefix}
      timeLimit={shopItem.timeLimit}
      stockLimit={shopItem.stockLimit}
      stockLimitLeft={shopItem.stockLimitLeft}
    />
  } else {
    return <ShopItem
      isLoading={isLoading}
      key={shopItem.name}
      name={shopItem.namePublicShop || shopItem.name}
      freeBonuses={shopItem.freeBonuses}
      saleBonus={shopItem.saleBonus}
      typeNames={shopItem.typeNames}
      mainImage={mainImage}
      hoverImage={hoverImage}
      priceObj={getShopItemPrice(shopItem)}
      price={formatShopItemOriginalPrice(shopItem)}
      discountPriceObj={shopItem.discountPrice}
      regularPriceObj={getShopItemOriginalPrice(shopItem)}
      discountPrice={formatShopItemDiscountPrice(shopItem)}
      hasSpecialOffer={false && shopItem.hasSpecialOffer}
      taxIncluded={shopItem.taxIncluded}
      optionalFor={shopItem.optionalFor && shopItem.optionalFor.itemTypeName}
      outOfStock={outOfStock}
      outOfStockMessage={outOfStockMessage}
      isNew={isComingSoon(shopItem)}
      isPreOrder={isPreOrder(shopItem)}
      productSetId={shopItem.productSetId}
      event={() => buildAddToCartEvent(shopItem)}
      dark={dark}
      isHighlighted={isHighlighted}
      pricePrefix={shopItem.pricePrefix}
      priority={index >= 0 && index < 4}
      colorVariants={colorVariants}
      currentColorName={currentColorName}
      region={region}
      endOfLife={shopItem.endOfLife}
      cardTag={shopItem.cardTag}
      cardTagVariant={shopItem.cardTagVariant}
      cardTagUntil={shopItem.cardTagUntil}
      itemType={shopItem.itemType && shopItem.itemType.itemType}
      saleDeal={shopItem.saleDeal}
    />
  }
}

export default Shop
